// src/services/api.js
import useAuthStore from '../stores/authStore';
import { normalizeResponse, extractErrorMessage } from '../utils/apiUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

// Get auth token from store
const getAuthToken = () => useAuthStore.getState().token;

// Basic error class for API errors
class ApiError extends Error {
  constructor(message, status = null, data = null) {
    super(message || 'An error occurred');
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Generic fetch wrapper with auth token
const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();
  console.log("Auth token retrieved for request:", token ? "Token exists" : "No token");
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,      
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
  
  // Debug headers
  console.log("Request headers:", defaultOptions.headers);
  // console.log("🚀 ~ fetchWithAuth ~ defaultOptions:", defaultOptions)
  
  // If the body is FormData, remove the Content-Type header
  if (options.body instanceof FormData) {
    console.log('[api] Detected FormData, removing Content-Type header');
    delete defaultOptions.headers['Content-Type'];
  }
  
  const fetchOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  // console.log("🚀 ~ fetchWithAuth ~ fetchOptions:", fetchOptions)
  
  try {
    console.log('[api] Making request to:', `${API_BASE_URL}${url}`);
    console.log('[api] Fetch options:', {
      ...fetchOptions,
      body: fetchOptions.body instanceof FormData ? 'FormData' : fetchOptions.body
    });
    
    const response = await fetch(`${API_BASE_URL}${url}`, fetchOptions);
    
    // Handle non-success responses
    if (!response.ok) {
      let errorData = {};
      
      // Try to parse error response as JSON
      try {
        errorData = await response.json();
      } catch (e) {
        // If parsing fails, use status text
        errorData = { message: response.statusText };
      }
      
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401 && token) {
        // Prevent refresh attempts for logout and refresh-token endpoints to avoid loops
        if (url.includes('/auth/logout') || url.includes('/auth/refresh-token')) {
          // If we're already trying to logout or refresh, don't trigger another cycle
          useAuthStore.getState().clearAuthState();
          return Promise.reject(new ApiError('Session expired. Please login again.', 401));
        }
        
        try {
          // Try to refresh token
          const refreshed = await useAuthStore.getState().refreshUserToken();
          
          // If token refresh successful, retry the original request
          if (refreshed) {
            // Get the new token after refresh
            const newToken = getAuthToken();
            // Update authorization header with new token
            fetchOptions.headers.Authorization = `Bearer ${newToken}`;
            const retryResponse = await fetch(`${API_BASE_URL}${url}`, fetchOptions);
            
            if (!retryResponse.ok) {
              let retryErrorData = {};
              try {
                retryErrorData = await retryResponse.json();
              } catch (e) {
                retryErrorData = { message: retryResponse.statusText };
              }
              
              // Return rejected promise instead of throwing
              return Promise.reject(new ApiError(
                retryErrorData.message || `Error: ${retryResponse.statusText}`,
                retryResponse.status,
                retryErrorData
              ));
            }
            
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
              const jsonResponse = await retryResponse.json();
              return normalizeResponse(jsonResponse);
            }
            
            return {};
          }
          
          // If token refresh failed, clear auth state silently (don't call logout API)
          useAuthStore.getState().clearAuthState();
          return Promise.reject(new ApiError('Session expired. Please login again.', 401));
        } catch (refreshError) {
          // If the refresh attempt itself fails
          useAuthStore.getState().clearAuthState();
          return Promise.reject(new ApiError('Authentication failed', 401));
        }
      }
      
      // For other error statuses, return rejected promise
      return Promise.reject(new ApiError(
        errorData.message || `Error: ${response.statusText}`,
        response.status,
        errorData
      ));
    }
    
    // Process successful response
    const contentType = response.headers.get('content-type');
    
    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }
    
    // Handle JSON responses
    if (contentType && contentType.indexOf('application/json') !== -1) {
      const jsonResponse = await response.json();
      return normalizeResponse(jsonResponse);
    }
    
    // Handle binary/blob responses (like Excel files)
    if (options.responseType === 'blob') {
      return response.blob();
    }
    
    // For other content types, return the response as is
    return response;
  } catch (error) {
    // Network errors or other exceptions
    const message = error.message || 'Network error occurred';
    console.error('API Error:', message, error);
    return Promise.reject(new ApiError(message));
  }
};

export const getSignedFileUrl = async (key, options = {}) => {
  try {
    const encodedKey = encodeURIComponent(key);
    let url = `/files/${encodedKey}/url`;
    
    // Add version parameter if provided
    if (options.version) {
      url += `?version=${options.version}`;
    }
    
    const response = await api.get(url);
    return response.signedUrl;
  } catch (error) {
    console.error("Error getting signed URL:", error);
    // Return rejected promise
    return Promise.reject(error);
  }
};

// HTTP Methods
export const api = {
  // GET request
  get: (url, options = {}) => fetchWithAuth(url, { method: 'GET', ...options }),
  
  // POST request with JSON or FormData body
  post: (url, data, options = {}) => fetchWithAuth(url, { 
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
    ...options
  }),
  postFiles: (url, data, options = {}) => fetchWithAuth(url, { 
    method: 'POST',
    body: data,
    ...options
  }),
  
  // PUT request with JSON body
  put: (url, data, options = {}) => fetchWithAuth(url, { 
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  }),
  
  // PATCH request with JSON body
  patch: (url, data, options = {}) => fetchWithAuth(url, { 
    method: 'PATCH',
    body: JSON.stringify(data),
    ...options
  }),
  
  // DELETE request
  delete: (url, options = {}) => fetchWithAuth(url, { 
    method: 'DELETE',
    ...options
  }),

  // File upload helper
  uploadFile: async (url, file, fieldName = 'file', additionalData = {}, options = {}) => {
    const formData = new FormData();
    formData.append(fieldName, file);
    
    // Add any additional data
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    return fetchWithAuth(url, {
      method: 'POST',
      body: formData,
      ...options
    });
  },
  
  // Cache invalidation helper
  invalidateCache: (url) => {
    console.log(`Cache invalidated for: ${url}`);
  },
  
  // Expose error class for use in components
  ApiError,
  
  // Tax API functions
  getTaxRegions: () => fetchWithAuth('/tax-config', { method: 'GET' }),
  getTaxRates: (regionCode) => fetchWithAuth(`/tax-config/region/${regionCode}`, { method: 'GET' }),
  getDefaultTaxRegion: () => fetchWithAuth('/settings/system/defaultTaxRegion', { method: 'GET' }),
  calculateTax: (configId, data) => fetchWithAuth(`/tax-config/${configId}/calculate`, { 
    method: 'POST',
    body: JSON.stringify(data)
  })
};